// useBreakpoint Hook
// LazySEM Frontend - Responsive breakpoint detection

import { useState, useEffect, useMemo } from "react";

type NavigatorWithConnection = Navigator & {
  connection?: EventTarget & {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };
};

interface BreakpointConfig {
  mobileXs: number;
  mobileSm: number;
  mobileMd: number;
  mobileLg: number;
  tabletSm: number;
  tabletMd: number;
  tabletLg: number;
  desktopSm: number;
  desktopMd: number;
  desktopLg: number;
}

interface BreakpointState {
  width: number;
  height: number;
  isMobileXs: boolean;
  isMobileSm: boolean;
  isMobileMd: boolean;
  isMobileLg: boolean;
  isMobile: boolean;
  isTabletSm: boolean;
  isTabletMd: boolean;
  isTabletLg: boolean;
  isTablet: boolean;
  isDesktopSm: boolean;
  isDesktopMd: boolean;
  isDesktopLg: boolean;
  isDesktop: boolean;
  orientation: "portrait" | "landscape";
  pixelRatio: number;
  isTouch: boolean;
  isRetina: boolean;
}

const defaultBreakpoints: BreakpointConfig = {
  mobileXs: 320,
  mobileSm: 375,
  mobileMd: 414,
  mobileLg: 480,
  tabletSm: 640,
  tabletMd: 768,
  tabletLg: 1024,
  desktopSm: 1280,
  desktopMd: 1440,
  desktopLg: 1920,
};

export function useBreakpoint(customBreakpoints?: Partial<BreakpointConfig>) {
  // Keep resize listeners alive across unrelated renders during their debounce.
  const breakpoints = useMemo(
    () => ({
      mobileXs: customBreakpoints?.mobileXs ?? defaultBreakpoints.mobileXs,
      mobileSm: customBreakpoints?.mobileSm ?? defaultBreakpoints.mobileSm,
      mobileMd: customBreakpoints?.mobileMd ?? defaultBreakpoints.mobileMd,
      mobileLg: customBreakpoints?.mobileLg ?? defaultBreakpoints.mobileLg,
      tabletSm: customBreakpoints?.tabletSm ?? defaultBreakpoints.tabletSm,
      tabletMd: customBreakpoints?.tabletMd ?? defaultBreakpoints.tabletMd,
      tabletLg: customBreakpoints?.tabletLg ?? defaultBreakpoints.tabletLg,
      desktopSm: customBreakpoints?.desktopSm ?? defaultBreakpoints.desktopSm,
      desktopMd: customBreakpoints?.desktopMd ?? defaultBreakpoints.desktopMd,
      desktopLg: customBreakpoints?.desktopLg ?? defaultBreakpoints.desktopLg,
    }),
    [
      customBreakpoints?.mobileXs,
      customBreakpoints?.mobileSm,
      customBreakpoints?.mobileMd,
      customBreakpoints?.mobileLg,
      customBreakpoints?.tabletSm,
      customBreakpoints?.tabletMd,
      customBreakpoints?.tabletLg,
      customBreakpoints?.desktopSm,
      customBreakpoints?.desktopMd,
      customBreakpoints?.desktopLg,
    ],
  );

  const [state, setState] = useState<BreakpointState>(() => {
    if (typeof window === "undefined") {
      return {
        width: 1024,
        height: 768,
        isMobileXs: false,
        isMobileSm: false,
        isMobileMd: false,
        isMobileLg: false,
        isMobile: false,
        isTabletSm: false,
        isTabletMd: true,
        isTabletLg: false,
        isTablet: true,
        isDesktopSm: false,
        isDesktopMd: false,
        isDesktopLg: false,
        isDesktop: false,
        orientation: "landscape",
        pixelRatio: 1,
        isTouch: false,
        isRetina: false,
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const pixelRatio = window.devicePixelRatio || 1;
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    return {
      width,
      height,
      isMobileXs: width <= breakpoints.mobileXs,
      isMobileSm: width <= breakpoints.mobileSm,
      isMobileMd: width <= breakpoints.mobileMd,
      isMobileLg: width <= breakpoints.mobileLg,
      isMobile: width <= breakpoints.tabletMd,
      isTabletSm: width >= breakpoints.tabletSm && width < breakpoints.tabletMd,
      isTabletMd: width >= breakpoints.tabletMd && width < breakpoints.tabletLg,
      isTabletLg:
        width >= breakpoints.tabletLg && width < breakpoints.desktopSm,
      isTablet: width > breakpoints.tabletMd && width < breakpoints.desktopSm,
      isDesktopSm:
        width >= breakpoints.desktopSm && width < breakpoints.desktopMd,
      isDesktopMd:
        width >= breakpoints.desktopMd && width < breakpoints.desktopLg,
      isDesktopLg: width >= breakpoints.desktopLg,
      isDesktop: width >= breakpoints.desktopSm,
      orientation: width > height ? "landscape" : "portrait",
      pixelRatio,
      isTouch,
      isRetina: pixelRatio > 1,
    };
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const pixelRatio = window.devicePixelRatio || 1;
      const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

      setState({
        width,
        height,
        isMobileXs: width <= breakpoints.mobileXs,
        isMobileSm: width <= breakpoints.mobileSm,
        isMobileMd: width <= breakpoints.mobileMd,
        isMobileLg: width <= breakpoints.mobileLg,
        isMobile: width <= breakpoints.tabletMd,
        isTabletSm:
          width >= breakpoints.tabletSm && width < breakpoints.tabletMd,
        isTabletMd:
          width >= breakpoints.tabletMd && width < breakpoints.tabletLg,
        isTabletLg:
          width >= breakpoints.tabletLg && width < breakpoints.desktopSm,
        isTablet: width > breakpoints.tabletMd && width < breakpoints.desktopSm,
        isDesktopSm:
          width >= breakpoints.desktopSm && width < breakpoints.desktopMd,
        isDesktopMd:
          width >= breakpoints.desktopMd && width < breakpoints.desktopLg,
        isDesktopLg: width >= breakpoints.desktopLg,
        isDesktop: width >= breakpoints.desktopSm,
        orientation: width > height ? "landscape" : "portrait",
        pixelRatio,
        isTouch,
        isRetina: pixelRatio > 1,
      });
    };

    // Debounce resize events
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener("resize", debouncedResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", debouncedResize);
      window.removeEventListener("orientationchange", handleResize);
      clearTimeout(timeoutId);
    };
  }, [breakpoints]);

  // Utility functions
  const isAtLeast = (breakpoint: keyof BreakpointConfig) => {
    return state.width >= breakpoints[breakpoint];
  };

  const isAtMost = (breakpoint: keyof BreakpointConfig) => {
    return state.width <= breakpoints[breakpoint];
  };

  const isBetween = (
    min: keyof BreakpointConfig,
    max: keyof BreakpointConfig,
  ) => {
    return state.width >= breakpoints[min] && state.width <= breakpoints[max];
  };

  const getCurrentBreakpoint = (): string => {
    if (state.isMobile) return "mobile";
    if (state.isTablet) return "tablet";
    if (state.isDesktop) return "desktop";
    return "unknown";
  };

  const getResponsiveValue = <T>(values: {
    mobile?: T;
    tablet?: T;
    desktop?: T;
    default: T;
  }): T => {
    if (state.isMobile && values.mobile !== undefined) return values.mobile;
    if (state.isTablet && values.tablet !== undefined) return values.tablet;
    if (state.isDesktop && values.desktop !== undefined) return values.desktop;
    return values.default;
  };

  return {
    ...state,
    breakpoints,
    isAtLeast,
    isAtMost,
    isBetween,
    getCurrentBreakpoint,
    getResponsiveValue,
  };
}

// Device detection utilities
export const useDeviceDetection = () => {
  const [deviceInfo /* setDeviceInfo */] = useState(() => {
    // setDeviceInfo not used
    if (typeof window === "undefined") {
      return {
        userAgent: "",
        isIOS: false,
        isAndroid: false,
        isSafari: false,
        isChrome: false,
        isFirefox: false,
        isEdge: false,
        isMobileDevice: false,
        isTabletDevice: false,
        isDesktopDevice: true,
      };
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    const isChrome = /chrome/.test(userAgent) && !/edge/.test(userAgent);
    const isFirefox = /firefox/.test(userAgent);
    const isEdge = /edge/.test(userAgent);

    // More sophisticated device detection
    const isMobileDevice = /mobi|android|touch|mini|phone/i.test(userAgent);
    const isTabletDevice =
      /tablet|ipad/i.test(userAgent) ||
      (isAndroid && !/mobile/i.test(userAgent));
    const isDesktopDevice = !isMobileDevice && !isTabletDevice;

    return {
      userAgent,
      isIOS,
      isAndroid,
      isSafari,
      isChrome,
      isFirefox,
      isEdge,
      isMobileDevice,
      isTabletDevice,
      isDesktopDevice,
    };
  });

  return deviceInfo;
};

// Network connection hook
export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState(() => {
    if (typeof navigator === "undefined" || !("connection" in navigator)) {
      return {
        isOnline: true,
        effectiveType: "4g" as const,
        downlink: 10,
        rtt: 100,
        saveData: false,
      };
    }

    const connection = (navigator as NavigatorWithConnection).connection;
    return {
      isOnline: navigator.onLine,
      effectiveType: connection?.effectiveType || "4g",
      downlink: connection?.downlink || 10,
      rtt: connection?.rtt || 100,
      saveData: connection?.saveData || false,
    };
  });

  useEffect(() => {
    const updateNetworkStatus = () => {
      if (typeof navigator === "undefined" || !("connection" in navigator)) {
        setNetworkStatus((prev) => ({ ...prev, isOnline: navigator.onLine }));
        return;
      }

      const connection = (navigator as NavigatorWithConnection).connection;
      setNetworkStatus({
        isOnline: navigator.onLine,
        effectiveType: connection?.effectiveType || "4g",
        downlink: connection?.downlink || 10,
        rtt: connection?.rtt || 100,
        saveData: connection?.saveData || false,
      });
    };

    window.addEventListener("online", updateNetworkStatus);
    window.addEventListener("offline", updateNetworkStatus);

    const connection = (navigator as NavigatorWithConnection).connection;
    if (connection) {
      connection.addEventListener("change", updateNetworkStatus);
    }

    return () => {
      window.removeEventListener("online", updateNetworkStatus);
      window.removeEventListener("offline", updateNetworkStatus);

      if (connection) {
        connection.removeEventListener("change", updateNetworkStatus);
      }
    };
  }, []);

  return networkStatus;
};
